import React from 'react';
import { DeckEvent, DeckEventType } from '../../components/decks/render';
import { beaconDeckEvent, postDeckEvent } from './deck_viewer_api';

const HEARTBEAT_MS = 15000;
/** Shorter glances (flicking past a slide) aren't recorded as a slide view. */
const MIN_SLIDE_MS = 400;

const uuid = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); }));

// #region sessionId
function sessionId(token: string): string {
    const key = `wd-deck-session:${token}`;
    try {
        const known = sessionStorage.getItem(key);
        if (known) return known;
        const id = uuid();
        sessionStorage.setItem(key, id);
        return id;
    } catch {
        return uuid();
    }
}

// #region device
function device(): DeckEvent['device'] {
    if (typeof window === 'undefined') return 'unknown';
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    return coarse || window.innerWidth < 768 ? 'mobile' : 'desktop';
}

// #region useDeckTracking
/**
 * Viewer analytics. `open` once per page load; `slide` with the visible time spent when leaving each slide;
 * `heartbeat` every 15 s while the page is visible; the current slide is flushed with sendBeacon on pagehide.
 * Does nothing when `enabled` is false (print mode, the sample, the editor's preview).
 */
export function useDeckTracking(token: string, enabled: boolean, slideId: string | null): { track: (type: DeckEventType, slide_id?: string) => void } {
    const session = React.useMemo(() => (enabled ? sessionId(token) : ''), [token, enabled]);
    const dev = React.useMemo(device, []);
    const current = React.useRef<{ id: string | null; ms: number; since: number | null }>({ id: null, ms: 0, since: null });

    const send = React.useCallback((e: Omit<DeckEvent, 'session' | 'device'>, beacon = false): void => {
        if (!enabled) return;
        const event: DeckEvent = { session, device: dev, ...e };
        if (beacon) beaconDeckEvent(token, event); else postDeckEvent(token, event);
    }, [enabled, session, dev, token]);

    /** Ends the timer on the current slide and records it. */
    const flush = React.useCallback((beacon = false): void => {
        const c = current.current;
        const ms = c.ms + (c.since !== null ? Date.now() - c.since : 0);
        if (c.id && ms >= MIN_SLIDE_MS) send({ type: 'slide', slide_id: c.id, ms: Math.round(ms) }, beacon);
        current.current = { id: c.id, ms: 0, since: document.visibilityState === 'visible' ? Date.now() : null };
    }, [send]);

    React.useEffect(() => { if (enabled) send({ type: 'open' }); }, [enabled, send]);

    React.useEffect(() => {
        if (!enabled) return;
        if (current.current.id && current.current.id !== slideId) flush();
        current.current = { id: slideId, ms: 0, since: document.visibilityState === 'visible' ? Date.now() : null };
    }, [enabled, slideId, flush]);

    React.useEffect(() => {
        if (!enabled) return;
        const onVisibility = (): void => {
            const c = current.current;
            if (document.visibilityState === 'hidden') {
                if (c.since !== null) { c.ms += Date.now() - c.since; c.since = null; }
            } else if (c.since === null) c.since = Date.now();
        };
        const onPageHide = (): void => flush(true);
        const beat = window.setInterval(() => {
            if (document.visibilityState === 'visible') send({ type: 'heartbeat', slide_id: current.current.id ?? undefined, ms: HEARTBEAT_MS });
        }, HEARTBEAT_MS);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.clearInterval(beat);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [enabled, send, flush]);

    const track = React.useCallback((type: DeckEventType, slide_id?: string) => send({ type, slide_id: slide_id ?? current.current.id ?? undefined }), [send]);
    return { track };
}
